/**
 * MUI Components whose styles are override as per theme
 * @param {JsonObject} theme Plain Json Object
 */
export default function componentStyleOverrides(theme) {
  return {
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: 44,
          borderRadius: '14px',
          fontWeight: 700,
          textTransform: 'none',
          letterSpacing: 0,
          boxShadow: 'none',
          transition: 'transform 160ms ease, box-shadow 160ms ease, background 160ms ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 14px 30px rgba(15, 140, 255, 0.18)'
          },
          '&:active': {
            transform: 'translateY(0)'
          }
        },
        containedPrimary: {
          color: '#ffffff',
          background: `linear-gradient(135deg, ${theme.colors.primaryMain} 0%, ${theme.colors.secondaryMain} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.colors.primaryDark} 0%, ${theme.colors.secondaryDark} 100%)`
          }
        },
        outlined: {
          borderWidth: '1.5px',
          background: 'rgba(255, 255, 255, 0.68)',
          backdropFilter: 'blur(16px)',
          '&:hover': {
            borderWidth: '1.5px'
          }
        }
      }
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        },
        rounded: {
          borderRadius: '18px'
        }
      }
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          color: theme.colors.textDark,
          padding: '24px 28px'
        },
        title: {
          fontSize: '1.125rem',
          letterSpacing: 0
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '24px'
        }
      }
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '24px'
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          color: theme.darkTextPrimary,
          paddingTop: '11px',
          paddingBottom: '11px',
          borderRadius: '14px',
          marginBottom: '4px',
          transition: 'all 180ms ease',
          '&.Mui-selected': {
            color: '#ffffff',
            background: `linear-gradient(135deg, ${theme.colors.primaryMain} 0%, ${theme.colors.secondaryMain} 100%)`,
            boxShadow: '0 14px 30px rgba(15, 140, 255, 0.22)',
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.colors.primaryDark} 0%, ${theme.colors.secondaryDark} 100%)`
            },
            '& .MuiListItemIcon-root': {
              color: '#ffffff'
            },
            '& .MuiTypography-root': {
              color: '#ffffff'
            }
          },
          '&:hover': {
            backgroundColor: 'rgba(15, 140, 255, 0.08)',
            color: theme.menuSelected,
            '& .MuiListItemIcon-root': {
              color: theme.menuSelected
            }
          }
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: theme.darkTextPrimary,
          minWidth: '38px'
        }
      }
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: theme.textDark
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          color: theme.textDark,
          '&::placeholder': {
            color: theme.darkTextSecondary,
            fontSize: '0.875rem'
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: 'rgba(255, 255, 255, 0.76)',
          borderRadius: '14px',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(15, 140, 255, 0.16)'
          },
          '&:hover $notchedOutline': {
            borderColor: theme.colors.primaryLight
          },
          '&.MuiInputBase-multiline': {
            padding: 1
          }
        },
        input: {
          fontWeight: 500,
          background: 'transparent',
          padding: '15.5px 14px',
          borderRadius: '14px',
          '&.MuiInputBase-inputSizeSmall': {
            padding: '10px 14px',
            '&.MuiInputBase-inputAdornedStart': {
              paddingLeft: 0
            }
          }
        },
        inputAdornedStart: {
          paddingLeft: 4
        },
        notchedOutline: {
          borderRadius: '14px'
        }
      }
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            color: theme.colors.grey300
          }
        },
        mark: {
          backgroundColor: theme.paper,
          width: '4px'
        },
        valueLabel: {
          color: theme.colors.primaryLight
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(102, 112, 133, 0.16)',
          opacity: 1
        }
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          color: theme.colors.primaryDark,
          background: `linear-gradient(135deg, ${theme.colors.primaryLight} 0%, ${theme.colors.secondaryLight} 100%)`
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          color: theme.paper,
          background: theme.colors.grey900,
          borderRadius: '10px'
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '999px',
          fontWeight: 700,
          letterSpacing: 0,
          '&.MuiChip-outlined': {
            background: 'rgba(255, 255, 255, 0.7)',
            borderColor: 'rgba(15, 140, 255, 0.2)'
          },
          '&.MuiChip-deletable .MuiChip-deleteIcon': {
            color: 'inherit'
          }
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(255, 255, 255, 0.72)',
          backdropFilter: 'blur(24px)',
          borderBottom: '1px solid rgba(15, 140, 255, 0.12)',
          boxShadow: '0 12px 40px rgba(7, 17, 31, 0.06)'
        }
      }
    }
  };
}
